
import { Component, Watch } from 'vue-property-decorator';
import ListBase from '@/lib/listbase';
import VWidget from '@/components/VWidget.vue';
import SelectedDashboard from './selected-dashboard/selected-dashboard.vue';
import DateRangePicker from '../components/date-range-picker.vue';

@Component({
    components: { VWidget, DateRangePicker, SelectedDashboard }
})
export default class Dashboard extends ListBase {
    rangeFrom: any = {
        from: 0,
        to: new Date().getMonth(),
        fromText: 'Enero',
        toText: this.gCommonList('months')[new Date().getMonth()].name,
        year: new Date().getFullYear() - 1
    };

    rangeTo: any = {
        from: 0,
        to: new Date().getMonth(),
        fromText: 'Enero',
        toText: this.gCommonList('months')[new Date().getMonth()].name,
        year: new Date().getFullYear()
    };

    tabModel: string = 'dashboard-dispatches';

    backendDealersBySchema: { dispatches: string[], daily: string[] } = { dispatches: [], daily: [] };
    selectedDealersBySchema: { dispatches: number[], daily: number[] } = { dispatches: [], daily: [] };

    dashboardSummaries: Record<string, any> = {};

    get currentSummary() {
        return this.tabModel === 'dashboard-dispatches'
            ? this.dashboardSummaries['dispatches']
            : this.dashboardSummaries['daily'];
    }

    async mounted() {
        await Promise.all([this.loadMonthlyData(), this.loadDailyData()]);
    }

    @Watch('tabModel')
    async onTabModelChanged(newTab: string) {
        if (newTab === 'dashboard-dispatches') {
            await this.loadMonthlyData();
        } else if (newTab === 'dashboard-daily') {
            await this.loadDailyData();
        }
    }

    private updateRangeValues(target: 'from' | 'to', data: any) {
        if (target === 'from') {
            this.rangeFrom = {
                from: data.rangeFrom,
                to: data.rangeTo,
                fromText: data.rangeFromMonth,
                toText: data.rangeToMonth,
                year: data.year
            };
            this.rangeTo = {
                ...this.rangeTo,
                from: data.rangeFrom,
                to: data.rangeTo,
                fromText: this.gCommonList('months')[data.rangeFrom].name,
                toText: this.gCommonList('months')[data.rangeTo].name
            };
        } else {
            this.rangeTo = {
                from: data.rangeFrom,
                to: data.rangeTo,
                fromText: data.rangeFromMonth,
                toText: data.rangeToMonth,
                year: data.year
            };
            this.rangeFrom = {
                ...this.rangeFrom,
                from: data.rangeFrom,
                to: data.rangeTo,
                fromText: this.gCommonList('months')[data.rangeFrom].name,
                toText: this.gCommonList('months')[data.rangeTo].name
            };
        }
    }

    async changeFrom(data: any) {
        this.updateRangeValues('from', data);
        await Promise.all([this.loadMonthlyData(), this.loadDailyData()]);
    }

    async changeTo(data: any) {
        this.updateRangeValues('to', data);
        await Promise.all([this.loadMonthlyData(), this.loadDailyData()]);
    }

    async updateYearFrom(year: number) {
        this.rangeFrom = { ...this.rangeFrom, year };
        await Promise.all([this.loadMonthlyData(), this.loadDailyData()]);
    }

    async updateYearTo(year: number) {
        this.rangeTo = { ...this.rangeTo, year };
        await Promise.all([this.loadMonthlyData(), this.loadDailyData()]);
    }

    setMonthFrom(index: number) {
        this.rangeFrom = {
            ...this.rangeFrom,
            from: index,
            to: index,
            fromText: this.gCommonList('months')[index].name,
            toText: this.gCommonList('months')[index].name
        };
    }

    setMonthTo(index: number) {
        this.rangeTo = {
            ...this.rangeTo,
            from: index,
            to: index,
            fromText: this.gCommonList('months')[index].name,
            toText: this.gCommonList('months')[index].name
        };
    }

    getInitials(str: string) {
        return str
            .split(' ')
            .map(n => n[0])
            .join('')
            .substring(0, 2);
    }

    private formatRange(range: any): string {
        return `${range.from}-${range.to}, ${range.year}`;
    }

    private getFormData() {
        return {
            date_range_start: this.formatRange(this.rangeFrom),
            date_range_end: this.formatRange(this.rangeTo),
        };
    }

    async loadData(schema: 'dispatches' | 'daily') {
        try {
            const formData = this.getFormData();
            const summary = await this.$store.dispatch(
                'dashboardTransactionModule/getSummary',
                { params: formData, schema }
            );

            this.dashboardSummaries[schema] = summary.Meses;
            this.backendDealersBySchema[schema] = summary.Dealers || [];

            if (this.backendDealersBySchema[schema].length > 0 &&
                this.selectedDealersBySchema[schema].length === 0) {
                const allDealerIds = this.backendDealersBySchema[schema].map((_, idx) => idx + 1);
                this.selectedDealersBySchema[schema] = allDealerIds;
            }
        } catch (error) {
            console.error(`Error loading ${schema} summary:`, error);
            this.$store.commit('app/setError', 'Error al obtener los datos del dashboard');
        }
    }

    async loadMonthlyData() {
        await this.loadData('dispatches');
    }

    async loadDailyData() {
        await this.loadData('daily');
    }

    async refreshDashboard() {
        await Promise.all([this.loadMonthlyData(), this.loadDailyData()]);
    }
}
